import React, { useMemo } from 'react';
import { Box, Paper, Typography, useTheme, useMediaQuery } from '@material-ui/core';
import { GOOGLE_ADS_CLIENT_ID, IS_DEV } from '../../../lib/constants';
import { useRemoteConfig } from '../../../lib/services/firebase.service';
import {Adsense} from '@ctrl/react-adsense';

type DeviceType = 'mobile' | 'tablet' | 'desktop';

interface GoogleAdsProps {
  adSlot?: string;
  adClient?: string;
  adFormat?: 'auto' | 'fluid' | 'rectangle' | 'vertical' | 'horizontal';
  adStyle?: { [key: string]: any };
  enableTesting?: boolean;
  testContent?: React.ReactNode;
  fallbackContent?: React.ReactNode;
  allowedDevices?: DeviceType[];
  showPlaceholderOnDisallowedDevices?: boolean;
  mockAdSlots?: boolean;
}

export const GoogleAdsComponent: React.FC<GoogleAdsProps> = ({
  adSlot = 'default-slot',
  adClient = `ca-pub-${GOOGLE_ADS_CLIENT_ID}`,
  adFormat = 'auto',
  adStyle = {},
  enableTesting = IS_DEV,
  testContent,
  fallbackContent,
  allowedDevices = ['desktop'], // Default to desktop-only
  showPlaceholderOnDisallowedDevices = false,
  mockAdSlots,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { googleAdsEnabled, googleAdTest, isLoading } = useRemoteConfig();

  // Get ad configuration from data or props
  const adConfig = {
    adSlot,
    adClient,
    adFormat,
    enableTesting,
    allowedDevices,
    showPlaceholderOnDisallowedDevices
  };

  // Determine current device type
  const getCurrentDeviceType = (): DeviceType => {
    if (isMobile) return 'mobile';
    if (isTablet) return 'tablet';
    return 'desktop';
  };

  const currentDevice = getCurrentDeviceType();
  const isDeviceAllowed = adConfig.allowedDevices.includes(currentDevice);

  // Responsive ad sizing
  const adDimensions = useMemo(() => {
    if (isMobile) {
      return {
        style: { width: '320px', height: '100px' }
      };
    } else if (isTablet) {
      return {
        style: { width: '728px', height: '90px' }
      };
    } else {
      return {
        style: { width: '300px', height: '250px' }
      };
    }
  }, [isMobile, isTablet])

  // Render test content during development
  const renderTestContent = () => {
    if (testContent) {
      return testContent;
    }

    const placeholderText = isDeviceAllowed
      ? '[AD PLACEHOLDER]'
      : `[AD DISABLED ON ${currentDevice.toUpperCase()}]`;

    const detailText = isDeviceAllowed
      ? 'Google Ads will appear here'
      : `Ads only shown on: ${adConfig.allowedDevices.join(', ')}`;

    return (
      <Paper
        elevation={1}
        style={{
          ...adDimensions.style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDeviceAllowed ? '#f5f5f5' : '#fff3e0',
          border: isDeviceAllowed ? '2px dashed #ccc' : '2px dashed #ff9800',
          margin: '8px auto'
        }}
      >
        <Box textAlign="center" p={2}>
          <Typography variant="caption" color="textSecondary">
            {placeholderText}
          </Typography>
          <Typography variant="body2" style={{ fontSize: '0.7rem', marginTop: '4px' }}>
            {adDimensions.style.width} x {adDimensions.style.height}
          </Typography>
          <Typography variant="caption" style={{ fontSize: '0.6rem', marginTop: '2px' }}>
            {detailText}
          </Typography>
        </Box>
      </Paper>
    );
  };

  // Render fallback content if ads fail to load
  const renderFallbackContent = () => {
    if (fallbackContent) {
      return fallbackContent;
    }

    return (
      <Box marginTop={40}>
        <Box
          style={{
            ...adDimensions.style,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '8px auto'
          }}
        >
          <Typography variant="caption" color="textSecondary">
            Advertisement
          </Typography>
        </Box>
      </Box>
    );
  };

  // Container styling with responsive margins
  const containerStyle = {
    textAlign: 'center' as const,
    margin: isMobile ? '8px 0' : isTablet ? '12px 0' : '16px 0',
    ...adStyle
  };

  // Defer until RC loads; googleAdsEnabled is the master gate.
  if (isLoading || !googleAdsEnabled) return null;

  // If device is not allowed and we shouldn't show placeholder, return null
  if (!isDeviceAllowed && !adConfig.showPlaceholderOnDisallowedDevices) {
    return null;
  }

  // If testing is enabled, show test content
  if (adConfig.enableTesting && mockAdSlots) {
    return (
      <Box style={containerStyle} {...props}>
        {renderTestContent()}
      </Box>
    );
  }

  // If device is not allowed but we should show placeholder, show fallback
  if (!isDeviceAllowed && adConfig.showPlaceholderOnDisallowedDevices) {
    return (
      <Box marginTop={40}>
        <Box style={containerStyle} {...props}>
          {renderFallbackContent()}
        </Box>
      </Box>
    );
  }

  // Render actual Google Ad (only if device is allowed)
  const {style: adDimensionsStyle} = adDimensions

  // Determine the data-adtest value
  // Firebase Remote Config value overrides local enableTesting setting
  const adTestValue = googleAdTest ?? !!adConfig.enableTesting;

  return (
    <Box marginTop={40}>
      <Box style={adDimensionsStyle} {...props}>
      <Adsense
        client={adConfig.adClient}
        slot={adConfig.adSlot}
        adTest={adTestValue ? "on" : "off"}
      />
      </Box>
    </Box>
  );
};

// Extend window object for TypeScript
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}
